"use strict";
import { meldSupportedCurrencyToCurrencyInfo } from "graphql/data/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getFiatCurrencyName,
  useAppFiatCurrency,
  useFiatCurrencyComponents
} from "uniswap/src/features/fiatCurrency/hooks";
import {
  useFiatOnRampAggregatorSupportedFiatCurrenciesQuery,
  useFiatOnRampAggregatorSupportedTokensQuery
} from "uniswap/src/features/fiatOnRamp/api";
import { getFiatCurrencyComponents } from "utilities/src/format/localeBased";
export const fallbackCurrencyInfo = {
  ...getFiatCurrencyComponents("en-US", "USD"),
  symbol: "$",
  name: "United States Dollar",
  shortName: "USD",
  code: "USD"
};
export function useMeldFiatCurrencyInfo(selectedCountry) {
  const { data: supportedFiatCurrencies } = useFiatOnRampAggregatorSupportedFiatCurrenciesQuery({
    countryCode: selectedCountry?.countryCode ?? "US"
  });
  const activeLocalCurrency = useAppFiatCurrency();
  const fiatCurrencyComponents = useFiatCurrencyComponents(activeLocalCurrency);
  const { t } = useTranslation();
  const appFiatCurrencySupported = supportedFiatCurrencies && supportedFiatCurrencies.fiatCurrencies.some(
    (currency) => activeLocalCurrency.toLowerCase() === currency.fiatCurrencyCode.toLowerCase()
  );
  const meldSupportedFiatCurrency = useMemo(() => {
    const { name, shortName } = getFiatCurrencyName(t, activeLocalCurrency);
    const activeLocalCurrencyFiatCurrencyInfo = {
      ...fiatCurrencyComponents,
      name,
      shortName,
      code: activeLocalCurrency
    };
    return appFiatCurrencySupported ? activeLocalCurrencyFiatCurrencyInfo : fallbackCurrencyInfo;
  }, [activeLocalCurrency, appFiatCurrencySupported, fiatCurrencyComponents, t]);
  return {
    meldSupportedFiatCurrency,
    notAvailableInThisRegion: supportedFiatCurrencies?.fiatCurrencies?.length === 0
  };
}
export function useFiatOnRampSupportedTokens(fiatCurrency, countryCode) {
  const { data: quoteCurrencyOptions } = useFiatOnRampAggregatorSupportedTokensQuery({
    fiatCurrency: fiatCurrency.code,
    countryCode: countryCode ?? "US"
  });
  return useMemo(() => {
    return quoteCurrencyOptions?.supportedTokens?.map((currency) => {
      const meldCurrencyCode = currency.cryptoCurrencyCode;
      const currencyInfo = meldSupportedCurrencyToCurrencyInfo(currency);
      return { currencyInfo, meldCurrencyCode };
    }) ?? [];
  }, [quoteCurrencyOptions?.supportedTokens]);
}
